import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  fillApplicationErrorMessage,
  type FillPerson,
  invalidateFillApplication,
  pickOwner,
} from '@/models/fillApplication'
import { OwnerChoiceList } from './OwnerChoiceList'

interface PickOwnerPanelProps {
  orgId: string
  owners: FillPerson[]
}

/** The Business owner section while several owners wait for staff to say who the application is for. */
export function PickOwnerPanel({ orgId, owners }: PickOwnerPanelProps) {
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  async function handlePick() {
    if (!selectedId) return
    setPending(true)
    try {
      await pickOwner(orgId, selectedId)
      // The pick can create the application, so the org (and the tab's key) refresh too, not just the state.
      await invalidateFillApplication(queryClient, orgId)
    } catch (error) {
      toast.error(fillApplicationErrorMessage(error))
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">
        This customer has {owners.length} business owners. Who is the application for?
      </p>
      <OwnerChoiceList owners={owners} selectedId={selectedId} onSelect={setSelectedId} />
      <div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void handlePick()}
          disabled={!selectedId}
          loading={pending ? 'Saving…' : undefined}
        >
          Use this owner
        </Button>
      </div>
    </div>
  )
}
